import React, { useCallback, MouseEvent } from 'react'

import { BorderStyle, Box, Color, Display, Space } from '@gousto-internal/citrus-react'
import { HotjarTrigger } from 'HotjarTrigger'
// eslint-disable-next-line @library/dependency-radar/valid-imports
import { Form, FormikProvider } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { CheckoutButton } from 'routes/Checkout/Components/CheckoutButton'
import { DeliveryEducationBanner } from 'routes/Checkout/Components/Delivery/DeliveryEducationBanner'
import { DeliveryInfoCard } from 'routes/Checkout/Components/DeliveryDetailsForm/DeliveryInfoCard'
import {
  FirstNameInput,
  LastNameInput,
  PhoneInput,
  DeliveryInstruction,
} from 'routes/Checkout/Components/DeliveryDetailsForm/Fields'
import { useDeliveryDetailsForm } from 'routes/Checkout/Components/DeliveryDetailsForm/useDeliveryDetailsForm'
import { PostcodeAddressFields } from 'routes/Checkout/Components/PostcodeAddressFields'
import { SectionHeader } from 'routes/Checkout/Components/SectionHeader'
import { trackCheckoutButtonPressed } from 'routes/Checkout/checkoutActions'
import { CheckoutStepIds } from 'routes/Checkout/checkoutConfig'
import { getIsMobile } from 'routes/Checkout/checkoutSelectors'

type DeliveryDetailsFormProps = {
  onStepChange: (stepId: CheckoutStepIds) => void
}

export const DeliveryDetailsForm = ({ onStepChange }: DeliveryDetailsFormProps) => {
  const dispatch = useDispatch()
  const isMobile = useSelector(getIsMobile)

  const formik = useDeliveryDetailsForm(onStepChange)

  const onCheckoutButtonClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()

      if (isMobile) {
        dispatch(
          trackCheckoutButtonPressed('DeliveryAddress Confirmed', {
            succeeded: formik.isValid,
            missing_field: formik.isValid
              ? null
              : Object.keys(formik.errors).filter((key) => Boolean((formik.errors as any)[key])),
          }),
        )
      }
      formik.submitForm()
    },
    [dispatch, formik, isMobile],
  )

  return (
    <Box
      data-testid="checkoutDeliverySection"
      borderStyle={BorderStyle.Solid}
      borderWidth={0.5}
      borderColor={Color.ColdGrey_100}
      bg={Color.White}
      paddingH={[3, 6]}
      paddingV={[6, 8]}
    >
      <SectionHeader title="Delivery details" />

      <DeliveryInfoCard />

      <FormikProvider value={formik}>
        <Form autoComplete="off">
          <Box display={Display.Flex}>
            <Box width="100%">
              <FirstNameInput />
            </Box>

            <Space size={4} direction="horizontal" />

            <Box width="100%">
              <LastNameInput />
            </Box>
          </Box>

          <Space size={6} direction="vertical" />

          <PostcodeAddressFields />

          <Box paddingBottom={[4, 6]}>
            <PhoneInput />
          </Box>

          <Box paddingBottom={[4, 6]}>
            <DeliveryInstruction />
          </Box>

          <DeliveryEducationBanner />

          <CheckoutButton isDisabled={!formik.isValid} onClick={onCheckoutButtonClick}>
            Continue to Payment
          </CheckoutButton>
        </Form>
      </FormikProvider>

      <HotjarTrigger name="pbc_delivery_and_payment" shouldInvoke={false} />
    </Box>
  )
}
